/**
 * Database enums used in Drizzle schema.
 * These are TypeScript enums that correspond to PostgreSQL enum types.
 */

export enum MembershipPlan {
  Standard = "Standard",
  Pro = "Pro",
  ProPlus = "ProPlus",
}

export enum CoachEmbeddingType {
  AboutMe = "AboutMe",
  Offer = "Offer",
  Testimonial = "Testimonial",
  PreferencesMatch = "PreferencesMatch",
}

export enum AppointmentStatus {
  Scheduled = "scheduled",
  Completed = "completed",
  Cancelled = "cancelled",
  NoShow = "noshow",
  Rescheduled = "rescheduled",
}

export enum ExpertiseType {
  Primary = "primary",
  Secondary = "secondary",
}

export enum CCOType {
  PAO = "pao",
  CTO = "cto",
}

/** A person's request to represent an organization, decided by DCM staff. */
export enum CcoAccessRequestStatus {
  Pending = "pending",
  Approved = "approved",
  Declined = "declined",
}

/** Which kind of public page an organization page change is about. */
export enum PageChangePageType {
  Organization = "organization",
  Program = "program",
}

/** What happened to an organization or program page edit. */
export enum PageChangeEvent {
  Submitted = "submitted",
  Published = "published",
  Returned = "returned",
  Discarded = "discarded",
}

/** Whether a page change came from DCM staff or an organization representative. */
export enum PageChangeActorRole {
  Staff = "staff",
  Representative = "representative",
}

/**
 * Where a coach's membership or program claim stands. Waiting until the
 * organization's representative or DCM staff decide it.
 */
export enum ClaimVerificationState {
  Waiting = "waiting",
  Verified = "verified",
  NotConfirmed = "not_confirmed",
}

export enum OfferType {
  Standard = "standard",
  Summit = "summit",
  MiniSprint = "mini_sprint",
}

export enum OfferStatus {
  Draft = "draft",
  Published = "published",
  Retired = "retired",
}

export enum RegistrationStatus {
  Open = "open",
  Closed = "closed",
}

export enum EngagementStatus {
  ACTIVE = "active",
  PAUSED = "paused",
  ARCHIVED = "archived",
  CANCELLED = "cancelled",
  FINISHED = "finished",
}

export enum SessionStatus {
  SCHEDULED = "scheduled",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  NO_SHOW = "no_show",
}

export enum TaskStatus {
  ACTIVE = "active",
  DONE = "done",
}

export enum NotificationType {
  HOMEWORK_ASSIGNED = "homework_assigned",
  NOTE_ADDED = "note_added",
  CHECK_IN_SUBMITTED = "check_in_submitted",
  CHECK_IN_REQUESTED = "check_in_requested",
  SESSION_RECAP = "session_recap",
  TESTIMONIAL_REQUESTED = "testimonial_requested",
  TESTIMONIAL_RECEIVED = "testimonial_received",
  WIN_LOGGED = "win_logged",
  GOAL_PROPOSED = "goal_proposed",
  SESSION_TIME_CHANGED = "session_time_changed",
  ENGAGEMENT_CREATED = "engagement_created",
  ENGAGEMENT_STATUS_CHANGED = "engagement_status_changed",
  INVOICE_AVAILABLE = "invoice_available",
  NEW_MESSAGE = "new_message",
  HOMEWORK_COMPLETED = "homework_completed",
  INVOICE_CHANGED = "invoice_changed",
  ARTICLE_RETURNED_TO_DRAFT = "article_returned_to_draft",
  ARTICLE_PUBLISHED = "article_published",
  ARTICLE_ARCHIVED = "article_archived",
  /** A feature the recipient voted for has shipped. Bell and push only, no email. */
  FEATURE_SHIPPED = "feature_shipped",
  NOVA_WEEKLY_MOVE_REMINDER = "nova_weekly_move_reminder",
}

/** A calendar service a coach can connect. Outlook joins as a second value. */
export enum CalendarProviderName {
  GOOGLE = "google",
}

/**
 * Where a calendar connection stands. `pending` is a sign-in that has not
 * come back yet; `needs_reconnect` is a connection the provider stopped
 * accepting (revoked access, changed password).
 */
export enum CalendarConnectionStatus {
  PENDING = "pending",
  ACTIVE = "active",
  NEEDS_RECONNECT = "needs_reconnect",
  DISCONNECTED = "disconnected",
}

/** What a `calendar_events` row mirrors: a DCM session or a booked call. */
export enum CalendarEventSubjectType {
  SESSION = "session",
  BOOKING = "booking",
}

/**
 * Whether the calendar holds what DCM last saved: `pending` waits for the
 * 15-minute retry, `failed` gave up (access lost, retries used up, or the
 * coach deleted the event in her calendar).
 */
export enum CalendarEventSyncState {
  SYNCED = "synced",
  PENDING = "pending",
  FAILED = "failed",
}

/** Who made a session's meeting link. Zoom joins as another value. */
export enum MeetingProvider {
  CUSTOM = "custom",
  GOOGLE_MEET = "google_meet",
}

/**
 * Where the client's calendar copy of a session comes from, for the whole
 * life of the session: DCM's invite file in its emails, or the invitation the
 * coach's calendar sent because the client is a guest on the event.
 */
export enum ClientCalendarSource {
  ICS = "ics",
  PROVIDER_INVITE = "provider_invite",
}
